// src/components/auth/ResetPassword.jsx  (React Router v5)
import React, { useMemo, useState } from "react";
import { Auth } from "aws-amplify";
import { useLocation, useHistory, Link } from "react-router-dom";
import "../../auth.css";

function errMsg(e) {
  if (!e) return "Something went wrong. Try again.";
  if (e.message) return e.message;
  return String(e);
}

// Helper for v5: read query string
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
  const location = useLocation();
  const history = useHistory();
  const qs = useQuery();

  const emailFromQS = qs.get("email") || "";
  const [email, setEmail] = useState(emailFromQS);
  const [code, setCode] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // show/hide password toggles
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  // Detect challenge mode (NEW_PASSWORD_REQUIRED)
  const challengeMode = location.state?.mode === "NEW_PASSWORD_REQUIRED";
  const challengeUser = location.state?.user || null;

  // Info banner text (only used in code-based flow)
  const initialSent = !!(location.state && location.state.sent);
  const [info, setInfo] = useState(
    !challengeMode && initialSent ? "We emailed a code to complete your reset." : ""
  );

  // === Password policy (mirror Cognito) ===
  const req = useMemo(() => {
    const length = pw1.length >= 12;
    const upper  = /[A-Z]/.test(pw1);
    const lower  = /[a-z]/.test(pw1);
    const number = /\d/.test(pw1);
    const symbol = /[^A-Za-z0-9]/.test(pw1);
    return { length, upper, lower, number, symbol };
  }, [pw1]);

  const allOk   = req.length && req.upper && req.lower && req.number && req.symbol;
  const matchOk = pw1.length > 0 && pw1 === pw2;

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || !pw1 || !pw2) {
      setError("Fill in all fields.");
      return;
    }
    if (!allOk) { setError("Your password does not meet the requirements."); return; }
    if (!matchOk) { setError("Passwords must match."); return; }

    setBusy(true);
    setError("");

    try {
      if (challengeMode) {
        // Force-change password path (no email code)
        if (!challengeUser) {
          setError("Your session expired. Please sign in again with your temporary password.");
          return;
        }
        await Auth.completeNewPassword(challengeUser, pw1);
        // Sign in natively after setting new password
        await Auth.signIn(email.trim(), pw1);
        history.replace("/");
      } else {
        // Code-based forgot password
        if (!code) { setError("Enter the verification code."); setBusy(false); return; }
        await Auth.forgotPasswordSubmit(email.trim(), code.trim(), pw1);
        // Sign in natively after reset
        try {
          await Auth.signIn(email.trim(), pw1);
          history.replace("/");
        } catch {
          history.replace({ pathname: "/signin", search: `?email=${encodeURIComponent(email.trim())}` });
        }
      }
    } catch (ex) {
      setError(errMsg(ex));
    } finally {
      setBusy(false);
    }
  }

  // Inline resend handler (only for code-based flow)
  async function resendCode(e) {
    e.preventDefault();
    if (challengeMode) return; // not applicable
    if (!email) {
      setError("Enter your email to resend the code.");
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await Auth.forgotPassword(email.trim());
      setInfo("If an account exists for that email, a new verification code has been sent.");
    } catch {
      setInfo("If an account exists for that email, a new verification code has been sent.");
    } finally {
      setBusy(false);
    }
  }

  const ReqItem = ({ ok, children }) => (
    <li className={`ph-req ${ok ? "ok" : "bad"}`}>
      <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
        {ok ? "✓" : "✕"}
      </span>
      <span>{children}</span>
    </li>
  );

  const score = (req.length + req.upper + req.lower + req.number + req.symbol);

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">
          {challengeMode ? "Set a New Password" : "Reset Password"}
        </h1>

        {challengeMode && !challengeUser && (
          <div className="ph-note" role="status">
            Session expired. Please sign in again with your temporary password.
          </div>
        )}

        {!challengeMode && info && <div className="ph-note" role="status">{info}</div>}
        {error && <div className="ph-error" role="alert">{error}</div>}

        <form onSubmit={onSubmit}>
          <label className="ph-label" htmlFor="rp-email">Email</label>
          <input
            id="rp-email"
            className="ph-field"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />

          {/* Code input only for code-based flow */}
          {!challengeMode && (
            <>
              <label className="ph-label" htmlFor="rp-code">Code</label>
              <input
                id="rp-code"
                className="ph-field"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                placeholder="Enter the 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
                onBlur={() => setCode(code.trim())}
              />
            </>
          )}

          <label className="ph-label" htmlFor="rp-new">New Password</label>
          <div className="ph-field-wrap">
            <input
              id="rp-new"
              className={`ph-field ${pw1 && !allOk ? "invalid" : ""}`}
              type={showPw1 ? "text" : "password"}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              autoComplete="new-password"
              placeholder="Enter a new password"
            />
            <button
              type="button"
              className="ph-toggle"
              onClick={() => setShowPw1((s) => !s)}
              aria-label={showPw1 ? "Hide password" : "Show password"}
              aria-pressed={showPw1}
              disabled={busy}
            >
              {showPw1 ? "Hide" : "Show"}
            </button>
          </div>

          {/* Strength meter */}
          <div className="ph-strength" aria-hidden="true">
            <div className="ph-strength-bar" style={{ width: `${score * 20}%` }} />
          </div>

          {/* Live password requirements */}
          <ul className="ph-req-list" aria-live="polite" aria-atomic="true" style={{ margin: "8px 0 12px" }}>
            <ReqItem ok={req.length}>At least 12 characters</ReqItem>
            <ReqItem ok={req.upper}>Contains an uppercase letter (A–Z)</ReqItem>
            <ReqItem ok={req.lower}>Contains a lowercase letter (a–z)</ReqItem>
            <ReqItem ok={req.number}>Contains a number (0–9)</ReqItem>
            <ReqItem ok={req.symbol}>Contains a symbol (e.g., !@#$%)</ReqItem>
          </ul>

          <label className="ph-label" htmlFor="rp-new2">Confirm New Password</label>
          <div className="ph-field-wrap">
            <input
              id="rp-new2"
              className="ph-field"
              type={showPw2 ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              placeholder="Re-enter new password"
              onKeyDown={(e) => e.key === "Enter" && !busy && onSubmit(e)}
            />
            <button
              type="button"
              className="ph-toggle"
              onClick={() => setShowPw2((s) => !s)}
              aria-label={showPw2 ? "Hide password" : "Show password"}
              aria-pressed={showPw2}
              disabled={busy}
            >
              {showPw2 ? "Hide" : "Show"}
            </button>
          </div>

          <div className={`ph-req ${matchOk ? "ok" : "bad"}`} aria-live="polite" style={{ margin: "6px 0 12px" }}>
            <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
              {matchOk ? "✓" : "✕"}
            </span>
            Passwords match
          </div>

          <button
            className="ph-button"
            disabled={
              busy || !email || !allOk || !matchOk || (!challengeMode && !code)
            }
            type="submit"
          >
            {busy ? "Changing…" : (challengeMode ? "Set Password" : "Change Password")}
          </button>
        </form>

        <p className="ph-hint" style={{ marginTop: 12 }}>
          {!challengeMode && (
            <>
              Didn’t get a code?{" "}
              <a href="#" className="ph-link" onClick={resendCode}>Resend</a>
              {"  ·  "}
            </>
          )}
          <Link className="ph-link" to="/signin">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
